import { MODAL_CALCULATOR_PROPS } from 'src/modules/user-input/components/FlowCalculatorModals.tsx';
import { MediumParams } from 'src/modules/user-input/components/MediumParams.tsx';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { reducer } from 'src/modules/user-input/calcV100/reducer.ts';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { targetUnits, targetUnitsAm } from 'src/modules/user-input/targetUnits.ts';
import { initialState } from 'src/modules/user-input/calcV100/initialState.ts';
import { MediumParamsChangeEventDetails } from 'src/modules/user-input/MediumParams/types.ts';
import { calcV100 } from 'src/modules/user-input/calcV100/calcV100.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalTopSection,
} from 'src/modules/common/components/CalculatorModalTopSection/CalculatorModalTopSection';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { Units } from 'uom-units';
import { RegionsEnum } from 'src/generated/operations.ts';
import { calcV100MethodB } from 'src/modules/user-input/calcV100/calcV100MethodB.ts';
import { CalculationMethod } from 'src/modules/user-input/components/CalculationMethod.tsx';
import { numberFormatter } from 'src/utils/numberFormatter.ts';

export const CalcV100 = ({
    handleModalValueChange,
    currentLocale,
    currentRegion,
    allowedUnits,
    targetUnit,
    uuid,
    title,
}: MODAL_CALCULATOR_PROPS) => {
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        ratedCapacityUnit: allowedUnits.ratedCapacity[0],
        temperatureDifferenceAirUnit: allowedUnits.temperatureDifference[0],
        temperatureDifferenceUnit: allowedUnits.temperatureDifference[1],
    });
    const [isMethodA, setMethod] = useState<boolean>(true);

    const onMediumParamsChange = useCallback((mediumParams: MediumParamsChangeEventDetails) => {
        dispatch({
            type: 'mediumParams_change',
            payload: mediumParams,
        });
    }, [dispatch]);

    const v100 = useMemo(() => {
        if (isMethodA) {
            return convert(
                calcV100(
                    convert(
                        state.ratedCapacity,
                        state.ratedCapacityUnit,
                        targetUnits.ratedCapacity,
                    ),
                    convert(
                        state.temperatureDifference,
                        state.temperatureDifferenceUnit,
                        targetUnits.temperatureDifference,
                    ),
                    state.heatCapacity,
                    state.density,
                ),
                Units.CubicMeterPerHour,
                targetUnit,
            );
        }
        return convert(
            calcV100MethodB(
                convert(
                    state.airflowVolume,
                    state.airflowVolumeUnit,
                    targetUnitsAm.volumetricFlowAir,
                ),
                state.temperatureDifferenceAir,
                state.temperatureDifference,
                state.heatCapacity,
                state.density,
            ),
            Units.GallonsPerMinute,
            targetUnit,
        );
    }, [state, targetUnit, isMethodA]);

    useEffect(() => {
        handleModalValueChange(v100);
    }, [v100, handleModalValueChange]);

    return (
        <CalculatorModalLayout>
            <CalculatorModalTopSection>
                <MediumParams
                    uuid={uuid}
                    currentRegion={currentRegion}
                    allowedUnits={allowedUnits}
                    currentLocale={currentLocale}
                    onChange={onMediumParamsChange}
                />
                {currentRegion === RegionsEnum.Am && (
                    <CalculationMethod isMethodA={isMethodA} setMethod={setMethod}/>
                )}
            </CalculatorModalTopSection>
            <CalculatorModalMainSection
                title={title}
                unit={targetUnit.name}
                result={numberFormatter(v100, currentLocale)}
                onClick={() => {
                    dispatch({ type: 'reset' });
                }}
            >
                {isMethodA && (
                    <>
                        <DecimalField
                            name="ratedCapacity"
                            inputSize="lg"
                            labelText={MESSAGES.RATED_CAP}
                            value={state.ratedCapacity}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.ratedCapacityUnit.name}
                                    values={allowedUnits.ratedCapacity}
                                    onChange={(payload) => {
                                        dispatch({ type: 'ratedCapacity_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'ratedCapacity_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                        <DecimalField
                            name="temperatureDifference"
                            inputSize="lg"
                            labelText={MESSAGES.TEMP_DIFF}
                            value={state.temperatureDifference}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.temperatureDifferenceUnit.name}
                                    values={[Units.Kelvin]}
                                    onChange={(payload) => {
                                        dispatch({ type: 'temperatureDifference_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'temperatureDifference_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                    </>
                )}
                {!isMethodA && (
                    <>
                        <DecimalField
                            name="ratedCapacity"
                            inputSize="lg"
                            labelText={MESSAGES.airflowVolume}
                            value={state.airflowVolume}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.airflowVolumeUnit.name}
                                    values={allowedUnits.airflowVolume}
                                    onChange={(payload) => {
                                        dispatch({ type: 'airflowVolume_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'airflowVolume_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                        <DecimalField
                            name="temperatureDifferenceAir"
                            inputSize="lg"
                            labelText={MESSAGES.TEMP_DIFF_AIR}
                            value={state.temperatureDifferenceAir}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.temperatureDifferenceAirUnit.name}
                                    values={allowedUnits.temperatureDifference}
                                    onChange={(payload) => {
                                        dispatch({ type: 'temperatureDifferenceAir_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'temperatureDifferenceAir_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                        <DecimalField
                            name="temperatureDifference"
                            inputSize="lg"
                            labelText={MESSAGES.TEMP_DIFF_WATER}
                            value={state.temperatureDifference}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.temperatureDifferenceUnit.name}
                                    values={allowedUnits.temperatureDifference}
                                    onChange={(payload) => {
                                        dispatch({ type: 'temperatureDifference_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'temperatureDifference_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                    </>
                )}
            </CalculatorModalMainSection>
        </CalculatorModalLayout>
    );
};
